import { describeError, useApi } from '@classgo/app-kit';
import type { GradesView } from '@classgo/shared';
import { Badge, Button, EmptyState, Input, Modal } from '@classgo/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { refusalOf } from '../../attendance/api';
import { gradebookApi } from '../../gradebook/api';
import type { GradesCell } from '../../gradebook/types';
import { useT } from '../../i18n';
import { recordQueries } from '../../records/api';
import {
  computedOf,
  decimalsOf,
  formatScore,
  parseScore,
  readingName,
  scoreIssue,
  studentName,
} from './support';
import type { GradeRow } from './types';

/**
 * The override of one student's period grade (#106): the computed grade stays in view, the
 * adjusted grade replaces it (empty returns to the computed one) and the observation explains
 * it; the report card prints the observation, so it can be edited alone. The API answers with
 * the book, which replaces the cached one.
 */
const AdjustModal = ({
  cell,
  view,
  row,
  onClose,
}: {
  cell: GradesCell;
  view: GradesView;
  row: GradeRow;
  onClose: () => void;
}) => {
  const t = useT();
  const api = useApi();
  const queries = useQueryClient();
  const decimals = decimalsOf(view.scale);
  const [score, setScore] = useState(
    row.overrideGrade === null || row.overrideGrade === undefined
      ? ''
      : formatScore(row.overrideGrade, decimals),
  );
  const [observation, setObservation] = useState(row.observation ?? '');
  const [issue, setIssue] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      gradebookApi(api).adjust({
        ...cell,
        studentId: row.studentId,
        overrideScore: parseScore(score),
        observation: observation.trim() === '' ? null : observation.trim(),
      }),
    onSuccess: (book) => {
      queries.setQueryData(
        recordQueries.grades(api, cell.groupId, cell.subjectId, cell.periodId).queryKey,
        book,
      );
      setNotice(t('gradebook.adjust.done', { student: readingName(row) }));
    },
  });
  const scoreProblem = scoreIssue(score, view.scale);
  const scoreError =
    scoreProblem === null
      ? undefined
      : t(`gradebook.scores.issue.${scoreProblem}`, {
          min: formatScore(view.scale?.minScore ?? null, decimals),
          max: formatScore(view.scale?.maxScore ?? null, decimals),
          decimals,
        });
  const submit = () => {
    setNotice(null);
    if (scoreProblem !== null) return;
    if (parseScore(score) !== null && observation.trim() === '') {
      setIssue(t('gradebook.adjust.observationRequired'));
      return;
    }
    setIssue(null);
    save.mutate();
  };
  return (
    <Modal
      open
      onClose={onClose}
      title={t('gradebook.adjust.title', { student: readingName(row) })}
      description={t('gradebook.adjust.computed', {
        grade: formatScore(computedOf(row), decimals),
      })}
      size="sm"
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button loading={save.isPending} onClick={submit}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input
          label={t('gradebook.adjust.score')}
          inputMode="decimal"
          value={score}
          onChange={(event) => {
            setScore(event.target.value);
            setNotice(null);
          }}
          help={t('gradebook.adjust.scoreHelp')}
          error={scoreError}
        />
        <div>
          <label htmlFor="adjust-observation" className="block text-sm font-medium text-gray-900">
            {t('gradebook.adjust.observation')}
          </label>
          <textarea
            id="adjust-observation"
            rows={3}
            maxLength={1000}
            aria-invalid={issue ? true : undefined}
            className="mt-1 w-full rounded-control border border-gray-300 p-2 text-sm"
            value={observation}
            onChange={(event) => {
              setObservation(event.target.value);
              setIssue(null);
              setNotice(null);
            }}
          />
          <p className="mt-1 text-xs text-gray-600">{t('gradebook.adjust.observationHelp')}</p>
          {issue ? (
            <p role="alert" className="mt-1 text-sm text-danger-700">
              {issue}
            </p>
          ) : null}
        </div>
        {notice ? (
          <p
            role="status"
            className="rounded-control bg-success-100 px-3 py-2 text-sm text-success-700"
          >
            {notice}
          </p>
        ) : null}
        {save.isError ? (
          <p
            role="alert"
            className="rounded-control bg-danger-100 px-3 py-2 text-sm text-danger-700"
          >
            {t('gradebook.adjust.failed')} {refusalOf(save.error) ?? describeError(t, save.error)}
          </p>
        ) : null}
      </div>
    </Modal>
  );
};

/**
 * The period grades of the cell (the M0 read screen, extended by #115): a score per
 * evaluation, the computed grade, the override, the period grade, the national label, the
 * observation and the publication; with `grades:write` and while unpublished, "Ajustar" per
 * student.
 */
export const PeriodTable = ({
  cell,
  view,
  canWrite,
}: {
  cell: GradesCell;
  view: GradesView;
  canWrite: boolean;
}) => {
  const t = useT();
  const decimals = decimalsOf(view.scale);
  const [adjusting, setAdjusting] = useState<string | null>(null);
  const adjustingRow = view.rows.find((row) => row.studentId === adjusting);
  const editable = canWrite && !view.published;
  if (view.rows.length === 0) return <EmptyState title={t('grades.view.empty')} />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" aria-label={t('grades.view.caption')}>
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
            <th className="pb-2 pr-3 font-semibold">{t('grades.view.column.student')}</th>
            {view.evaluations.map((evaluation) => (
              <th key={evaluation.id} className="pb-2 pr-3 text-right font-semibold">
                {evaluation.name}
                <span className="block text-xs normal-case tracking-normal text-gray-500">
                  {t('grades.view.weight', { weight: evaluation.weight })}
                </span>
              </th>
            ))}
            <th className="pb-2 pr-3 text-right font-semibold">{t('gradebook.column.computed')}</th>
            <th className="pb-2 pr-3 text-right font-semibold">{t('gradebook.column.override')}</th>
            <th className="pb-2 pr-3 text-right font-semibold">{t('grades.view.column.final')}</th>
            <th className="pb-2 pr-3 font-semibold">{t('grades.view.column.national')}</th>
            <th className="pb-2 pr-3 font-semibold">{t('gradebook.column.observation')}</th>
            <th className="pb-2 pr-3 font-semibold">{t('grades.view.column.published')}</th>
            {editable ? (
              <th className="pb-2 text-right font-semibold">{t('gradebook.column.actions')}</th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {view.rows.map((row) => (
            <tr key={row.studentId} className="border-t border-gray-100">
              <td className="py-2 pr-3 font-medium text-gray-900">{studentName(row)}</td>
              {view.evaluations.map((evaluation) => (
                <td key={evaluation.id} className="py-2 pr-3 text-right tabular-nums">
                  {formatScore(row.scores[evaluation.id] ?? null, decimals)}
                </td>
              ))}
              <td className="py-2 pr-3 text-right tabular-nums text-gray-600">
                {formatScore(computedOf(row), decimals)}
              </td>
              <td className="py-2 pr-3 text-right tabular-nums text-gray-600">
                {formatScore(row.overrideGrade ?? null, decimals)}
              </td>
              <td
                className={`py-2 pr-3 text-right font-semibold tabular-nums ${view.scale && row.periodGrade !== null && row.periodGrade < view.scale.passMark ? 'text-danger-700' : 'text-gray-900'}`}
              >
                {formatScore(row.periodGrade, decimals)}
              </td>
              <td className="py-2 pr-3">{row.nationalLabel ?? t('common.none')}</td>
              <td className="max-w-xs py-2 pr-3 text-gray-600">
                {row.observation ?? t('common.none')}
              </td>
              <td className="py-2 pr-3">
                <Badge tone={row.published ? 'success' : 'gray'}>
                  {row.published ? t('grades.view.published') : t('grades.view.draft')}
                </Badge>
              </td>
              {editable ? (
                <td className="py-1 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`${t('gradebook.adjust')}: ${readingName(row)}`}
                    onClick={() => setAdjusting(row.studentId)}
                  >
                    {t('gradebook.adjust')}
                  </Button>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
      {adjustingRow ? (
        <AdjustModal
          cell={cell}
          view={view}
          row={adjustingRow}
          onClose={() => setAdjusting(null)}
        />
      ) : null}
    </div>
  );
};
